<template>
  <transition name="ip-fade">
    <div v-if="value" class="ip-overlay" @click.self="$emit('input', false)">
      <div class="ip-panel">
        <!-- Header -->
        <div class="ip-header">
          <div class="ip-header__left">
            <span class="ip-header__icon">📱</span>
            <div>
              <h3 class="ip-title">Instancias de WhatsApp</h3>
              <p class="ip-subtitle">Enlazá cada instancia con una sección</p>
            </div>
          </div>
          <button class="ip-close" title="Cerrar" @click="$emit('input', false)">✕</button>
        </div>

        <!-- Body -->
        <div class="ip-body">
          <div v-if="loading" class="ip-state">
            <span class="ip-spinner"></span>
            <span>Cargando instancias…</span>
          </div>
          <div v-else-if="error" class="ip-state ip-state--error">
            <span>⚠️ {{ error }}</span>
            <button class="ip-retry" @click="fetchInstances">Reintentar</button>
          </div>
          <div v-else-if="instances.length === 0" class="ip-state">
            No hay instancias registradas.
          </div>

          <div v-else class="ip-list">
            <div v-for="inst in instances" :key="inst.id" class="ip-item">
              <div class="ip-item__main">
                <span class="ip-conn" :class="stateClass(inst.estado_conexion)" :title="inst.estado_conexion || 'desconocido'"></span>
                <div class="ip-item__info">
                  <span class="ip-item__name">{{ inst.nombre }}</span>
                  <span v-if="inst.profile_name" class="ip-item__profile">{{ inst.profile_name }}</span>
                </div>
                <span class="ip-item__state">{{ inst.estado_conexion || 'desconocido' }}</span>
              </div>
              <div class="ip-item__assign">
                <label class="ip-item__assign-lbl">Sección</label>
                <select
                  class="ip-select"
                  :value="inst.id_dueño != null ? String(inst.id_dueño) : ''"
                  :disabled="savingId === inst.id"
                  @change="onAssign(inst, $event.target.value)"
                >
                  <option value="">— Sin sección —</option>
                  <option v-for="s in sections" :key="s.id" :value="String(s.id)">{{ s.label || s.nombre }}</option>
                </select>
                <span v-if="savingId === inst.id" class="ip-mini-spinner"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import apiUrls from '@/new-constants/apiUrls'

export default {
  name: 'InstancesPopup',
  props: {
    value: { type: Boolean, default: false },
    baseUrl: { type: String, default: apiUrls.base },
    // Secciones disponibles para enlazar (vienen del dashboard).
    sections: { type: Array, default: () => [] }
  },
  data() {
    return {
      instances: [],
      loading: false,
      error: null,
      savingId: null
    }
  },
  watch: {
    value(v) { if (v) this.fetchInstances() }
  },
  methods: {
    async fetchInstances() {
      this.loading = true
      this.error = null
      try {
        const token = localStorage.getItem('token') || ''
        const res = await fetch(`${this.baseUrl}support/instances`, {
          headers: { Accept: 'application/json', Authorization: token }
        })
        const json = await res.json()
        this.instances = (json && json.data) ? json.data : []
      } catch (e) {
        this.error = 'No se pudieron cargar las instancias.'
      } finally {
        this.loading = false
      }
    },
    async onAssign(inst, sectionId) {
      if (this.savingId) return
      this.savingId = inst.id
      try {
        const token = localStorage.getItem('token') || ''
        const res = await fetch(`${this.baseUrl}support/instances/${inst.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: token },
          body: JSON.stringify({ section_id: sectionId ? Number(sectionId) : null })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo actualizar la instancia.')

        // Reflejar el cambio localmente: la instancia elegida toma la sección y
        // se libera cualquier otra que apuntaba a esa misma sección.
        if (sectionId) {
          this.instances.forEach(i => {
            if (String(i.id_dueño) === String(sectionId)) i.id_dueño = null
          })
        }
        inst.id_dueño = sectionId ? sectionId : null
        // Avisar al dashboard para que refresque los indicadores de las secciones.
        this.$emit('changed')
      } catch (e) {
        this.error = e.message || 'No se pudo actualizar la instancia.'
        // Recargar para volver al estado real del servidor.
        this.fetchInstances()
      } finally {
        this.savingId = null
      }
    },
    stateClass(estado) {
      const e = String(estado || '').trim().toLowerCase()
      if (['open', 'conectado', 'connected', 'online'].includes(e)) return 'ip-conn--on'
      if (['connecting', 'conectando', 'pairing'].includes(e)) return 'ip-conn--warn'
      return 'ip-conn--off'
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
.ip-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(10, 10, 11, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.ip-panel {
  width: 100%;
  max-width: 560px;
  max-height: 82vh;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Geist', 'DM Sans', 'Segoe UI', system-ui, sans-serif;
  color: #0A0A0B;
}
.ip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #ECEAE4;
}
.ip-header__left { display: flex; align-items: center; gap: 12px; }
.ip-header__icon {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  border-radius: 11px;
  background: rgba(252, 112, 16, 0.10);
  border: 1px solid rgba(252, 112, 16, 0.22);
}
.ip-title { margin: 0; font-size: 15px; font-weight: 800; letter-spacing: -0.2px; }
.ip-subtitle { margin: 2px 0 0; font-size: 12px; color: #6E6E76; }
.ip-close {
  width: 30px; height: 30px;
  border-radius: 8px;
  background: #FAFAF9;
  border: 1px solid #DBD9D2;
  cursor: pointer;
  color: #A0A0A8;
  font-size: 13px;
}
.ip-close:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

.ip-body { flex: 1; overflow-y: auto; padding: 12px 14px; min-height: 0; }

.ip-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; padding: 40px 16px; color: #6E6E76; font-size: 13px;
}
.ip-state--error { color: #dc2626; }
.ip-retry {
  padding: 6px 14px; border-radius: 8px; border: 1px solid #DBD9D2;
  background: #fff; cursor: pointer; font-size: 12px; font-weight: 700;
}

.ip-list { display: flex; flex-direction: column; gap: 8px; }
.ip-item {
  border: 1px solid #ECEAE4;
  border-radius: 12px;
  padding: 11px 12px;
  background: #FAFAF9;
}
.ip-item__main { display: flex; align-items: center; gap: 10px; }
.ip-item__info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ip-item__name { font-weight: 700; font-size: 13px; }
.ip-item__profile { font-size: 11px; color: #6E6E76; }
.ip-item__state { font-size: 11px; color: #6E6E76; text-transform: capitalize; }

.ip-conn { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.ip-conn--on   { background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.15); }
.ip-conn--warn { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
.ip-conn--off  { background: #A0A0A8; box-shadow: 0 0 0 3px rgba(156,163,175,0.15); }

.ip-item__assign { display: flex; align-items: center; gap: 8px; margin-top: 9px; }
.ip-item__assign-lbl { font-size: 11px; font-weight: 700; color: #6E6E76; }
.ip-select {
  flex: 1;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid #DBD9D2;
  background: #fff;
  font-size: 12.5px;
  font-family: inherit;
  color: #0A0A0B;
  outline: none;
}
.ip-select:focus { border-color: #FC7010; box-shadow: 0 0 0 3px rgba(252,112,16,0.12); }
.ip-select:disabled { opacity: 0.5; }

.ip-spinner, .ip-mini-spinner {
  border-radius: 50%;
  border: 2.5px solid #ECEAE4;
  border-top-color: #FC7010;
  animation: ip-spin 0.75s linear infinite;
}
.ip-spinner { width: 22px; height: 22px; }
.ip-mini-spinner { width: 14px; height: 14px; border-width: 2px; }
@keyframes ip-spin { to { transform: rotate(360deg); } }

.ip-fade-enter-active, .ip-fade-leave-active { transition: opacity 0.18s ease; }
.ip-fade-enter, .ip-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .ip-panel { max-width: 100%; max-height: 92vh; }
}
</style>
